import React, { Component } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import moment from 'moment'
import { compose, pure } from 'recompose'
import { connect } from 'react-redux'
import { notification } from 'redux/modules/app'
import { AwardOverview } from 'containers/sections/awards'
import { ParticipantParent, ParticipantPayment, ParticipantProfileQuestions } from 'components/sections/participants'
import { ParticipantAwardUnitChange, ParticipantLeader } from 'containers/forms/participants'
import { form } from 'helpers/decorators'
import { AwardRegistrationDate } from 'components/sections/awards'
import Checkbox from 'components/fields/Checkbox/Checkbox'
import { Form } from 'components/layout'
import { ButtonGroup, FloatingBar, FormButton } from 'components/misc'
import { apiCodeLists, apiConfiguration, apiLabels, apiParticipants } from 'helpers/decorators/api'
import validate from 'models/awards/validateUpdate'
import { getActiveRole } from 'utils/token'
import { CONFIGURATION_KEYS, LEVELS, ROLES } from 'constants/enums'
import { Input, Textarea } from '../../../../components/fields'
import LabelsOverview from '../../../../components/sections/awards/LabelsOverview/LabelsOverview'

const enhance = compose(
  pure,
  apiParticipants({ keepDataOnUnmount: true }),
  apiConfiguration(),
  apiLabels(),
  apiCodeLists(['consentDeliveryTypes', 'participantPaymentStates']),
  form({
    initialProps: 'award',
    reinitialize: true,
    validate,
  }),
  connect(
    ({ auth, api }) => ({
      activeOrganization: auth.organization,
      labels: api.labels,
      availableLabels: api.labels.available.list,
      awardLabels: api.labels.award.list
    }),
    {
      notification,
    }
  )
)

class AwardDetail extends Component {
  static propTypes = {
    award: PropTypes.object,
    refreshAward: PropTypes.func,
    awardGenerateReport: PropTypes.func,
    awards: PropTypes.object,
    change: PropTypes.func,
    codeLists: PropTypes.object,
    handleSubmit: PropTypes.func,
    invalid: PropTypes.bool,
    submitting: PropTypes.bool,
    modal: PropTypes.func,
    permissions: PropTypes.object,
    pristine: PropTypes.bool,
    onUpdateAward: PropTypes.func,
    resendParentalConsentLink: PropTypes.func,
    submitFailed: PropTypes.bool,
    submitSucceeded: PropTypes.bool,
    values: PropTypes.object,
    participants: PropTypes.object,
    initialize: PropTypes.func,
    configuration: PropTypes.object.isRequired,
    onGetOrganizationParentalConsentConfiguration: PropTypes.func.isRequired,
    notification: PropTypes.func.isRequired,
    activeOrganization: PropTypes.object,

    onGetConfigurationValue: PropTypes.func,
    awardLabels: PropTypes.object,
    labels: PropTypes.object,
    availableLabels: PropTypes.object,
    onFetchAvailableLabels: PropTypes.func,
    onAssignAwardLabel: PropTypes.func,
    onUnassignAwardLabel: PropTypes.func,
    onGetAwardLabels: PropTypes.func
  }

  componentDidMount() {
    this.props.onGetConfigurationValue(CONFIGURATION_KEYS.enableAwardLabels.id, get(this.props, 'award.organization.id'))
    const awardId = get(this.props, 'award.id')
    const organizationId = get(this.props, 'award.organization.id')
    awardId && organizationId && this.props.onFetchAvailableLabels(null, organizationId, null, awardId)
    awardId && this.props.onGetAwardLabels(awardId)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.award.id !== this.props.award.id) {
      this.props.initialize(nextProps.award)
    }

    const awardId = get(nextProps, 'award.id')
    const organizationId = get(nextProps, 'award.organization.id')

    if (get(this.props, 'award.id') !== awardId) {
      this.props.onGetConfigurationValue(CONFIGURATION_KEYS.enableAwardLabels.id, organizationId)
      awardId && organizationId && this.props.onFetchAvailableLabels(awardId, organizationId)
      awardId && this.props.onGetAwardLabels(awardId)
    }
  }

  handleSubmit = fields =>
    this.props.onUpdateAward(fields, get(this.props, 'award.id'), {
      initialData: this.props.award,
    })

  getProfileQuestions = (awardId, activeOrganization, configuration) => {
    if (awardId) {
      const isLeader = getActiveRole(activeOrganization) === ROLES.LEADER.u
      const isAllowedShowProfileQuestions = get(configuration, `valuesByOrganizationId[${activeOrganization.id}][${CONFIGURATION_KEYS.allowGetProfileQuestionAnswers.id}].booleanValue`)

      if (isLeader && !isAllowedShowProfileQuestions) {
        return
      }

      return <ParticipantProfileQuestions awardId={awardId} />
    }
  }

  getBulkLogsForAward = award => {
    const awardLevel = get(award, 'awardLevel.value')

    const orgId = get(award, 'organization.id')

    switch(awardLevel) {
      case LEVELS.BRONZE.value: return get(this.props.configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.isBulkLogsForBronzeEnabled.id}].booleanValue`)
      case LEVELS.SILVER.value: return get(this.props.configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.isBulkLogsForSilverEnabled.id}].booleanValue`)
      case LEVELS.GOLD.value: return get(this.props.configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.isBulkLogsForGoldEnabled.id}].booleanValue`)
      default: return false
    }
  }

  render() {
    const {
      award,
      refreshAward,
      awardGenerateReport,
      participants,
      awards,
      change,
      codeLists,
      handleSubmit,
      invalid,
      modal,
      permissions,
      pristine,
      submitting,
      resendParentalConsentLink,
      submitFailed,
      submitSucceeded,
      values,
      activeOrganization,
      configuration,
      awardLabels,
      labels,
      availableLabels,
      onFetchAvailableLabels,
      onAssignAwardLabel,
      onUnassignAwardLabel,
      onGetAwardLabels
    } = this.props

    const olderThan18 =
      get(award, 'participant.birthDate') &&
      moment().diff(award.participant.birthDate, 'years') >= 18

    const fieldPrefix = ''

    const participantAward =
      get(awards, 'list', []).find(item => item.id === get(award, 'id')) ||
      get(participants, 'detail.awards', []).find(item => item.id === get(award, 'id'))

    const awardLeader = get(participantAward, 'awardLeader')

    const isBulkLogsEnabled = get(configuration, `valuesByOrganizationId[${get(participantAward, 'organization.id')}][${CONFIGURATION_KEYS.isBulkLogsEnabled.id}].booleanValue`)

    const isBulkLogsForAwardEnabled = this.getBulkLogsForAward(participantAward)

    const orgId = get(participantAward, 'organization.id')

    const isLabelsEnabled = get(configuration, `valuesByOrganizationId[${orgId}][${CONFIGURATION_KEYS.enableAwardLabels.id}].booleanValue`)

    return (
      <Form
        className="form"
        onSubmit={handleSubmit(this.handleSubmit)}
        encType="multipart/form-data"
        isPerson
      >
        <div className="row base-margin--top">
          <div className="col-xs-12 col-sm-6">
            <AwardRegistrationDate />

            <div className="row">
              <div className="col-xs-12">
                <Textarea
                    label="Note"
                    intlKey="label.note"
                    name="note"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-xs-12">
                <Input
                  label="Digital Badge URL"
                  intlKey="label.digital_badge_url"
                  name="digitalBadgeUrl"
                />
              </div>
            </div>

            <ParticipantParent
              key={award.organization.id}
              consentDeliveryTypes={codeLists.consentDeliveryTypes.list}
              data={award}
              disabled={olderThan18}
              fieldPrefix={fieldPrefix}
              modal={modal}
              onGetOrganizationParentalConsentConfiguration={
                this.props.onGetOrganizationParentalConsentConfiguration
              }
              configuration={this.props.configuration}
              resendParentalConsentLink={resendParentalConsentLink}
              notification={this.props.notification}
            />

            <ParticipantPayment
              change={change}
              data={award}
              fieldPrefix={fieldPrefix}
              participantPaymentStates={codeLists.participantPaymentStates.list}
              primary={false}
            />
          </div>



          <div className="col-xs-12 col-sm-6">
            <AwardOverview
              awardId={get(award, 'id')}
              awardType={get(award, 'awardType', {})}
              awardState={get(award, 'awardState', {})}
              awardMode={get(award, 'awardMode', {})}
              organizationMode={get(award, 'organization.organizationMode', {})}
              awardOfficeSignoffDate={get(award, 'officeSignoffDate')}
              awardCeremonyDate={get(award, 'ceremonyDate')}
              awardLeaderSignoffDate={get(award, 'leaderSignoffDate')}
              currentAwardLevel={get(award, 'awardLevel')}
              isReportLoading={awards.report.loading}
              onGenerateAwardReport={(id, type) => awardGenerateReport(get(award, 'id'), type)}
              permissions={permissions}
            />

            <ParticipantAwardUnitChange
              data={participantAward}
              refreshAward={refreshAward}
              decoOrganizationHierarchyLevel={get(
                values,
                `${fieldPrefix}organization.organizationHierarchyLevel.id`
              )}
              fieldPrefix={fieldPrefix}
            />

            <ParticipantLeader
              awardLeader={awardLeader}
              refreshAward={refreshAward}
              awardId={award.id}
              orgId={get(participantAward, 'organization.id')}
              submitFailed={submitFailed}
            />

            {isLabelsEnabled && <LabelsOverview
              data={awardLabels}
              labels={labels}
              availableLabels={availableLabels}
              onFetchAvailableLabels={onFetchAvailableLabels}
              onAssignAwardLabel={onAssignAwardLabel}
              onUnassignAwardLabel={onUnassignAwardLabel}
              onGetAwardLabels={onGetAwardLabels}
              awardId={get(participantAward, 'id')}
              organizationId={orgId}
            />}

            <div className="row">
              <div className="col-xs-12">
                <Checkbox
                  label="As an Award Participant I am happy to participate in the Foundation's Participant Satisfaction Surveys"
                  intlKey="label.participant_survey"
                  name="isGivenSurveyConsent"
                />
                {isBulkLogsEnabled && isBulkLogsForAwardEnabled && <div className="row">
                <div className="col-xs-12">
                  <Checkbox
                    label="Allow adding logs in bulk"
                    intlKey="label.allow_adding_logs_in_bulk"
                    name="isBulkLogEnabled"
                  />
                </div>
              </div>}
              </div>
            </div>
          </div>
        </div>

        {this.getProfileQuestions(get(award, 'id'), activeOrganization, configuration)}

        <FloatingBar>
          <ButtonGroup right>
            <FormButton
              pristine={pristine}
              submitSucceeded={submitSucceeded}
              submitFailed={submitFailed}
              invalid={invalid}
              isLoading={submitting}
              type="submit"
              intlKey="button.save"
            >
              Save
            </FormButton>
          </ButtonGroup>
        </FloatingBar>
      </Form>
    )
  }
}

export default enhance(AwardDetail)
